import React, { FC } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import NavigationMenu from '../NavigationMenu';
import { getRoutes } from './getRoutes';
import { Header } from '../Header';
import styles from './styles.module.css';
import { useAuth0 } from '../../../Auth';
import { StopScreen } from '../StopScreen/StopScreen';
import { RewardsEnrollmentProviderType } from 'Shared/hooks/useRewardsEnrollment';
import {
  IncentiveProgramEnrollmentProviderType,
  IncentiveProgramEnrollmentResponse,
} from 'Shared/hooks/useIncentiveProgramEnrollment/useIncentiveProgramEnrollment';
import { useRewardsEnrollmentContext } from 'Shared/providers/RewardsEnrollmentProvider';
import { LoadingPage } from 'Shared/components';
import { useSurvicate } from 'Shared/hooks/useSurvicate/useSurvicate';

export const Core: FC = () => {
  useSurvicate();

  const { user } = useAuth0();

  const {
    loading,
    incentiveProgramEnrollmentResponse,
    rewardsEnrollmentResponse,
    refresh,
  } = useRewardsEnrollmentContext();

  if (!user || loading) {
    return <LoadingPage />;
  }

  // Adapter objects so getRoutes' existing predicate shape continues to
  // work. The provider has already settled `loading`, so the inner
  // *Loading flags are always false here.
  const incentiveProgramEnrollment: IncentiveProgramEnrollmentProviderType = {
    incentiveProgramEnrollmentResponse,
    incentiveProgramEnrollmentLoading: false,
    refreshIncentiveProgramEnrollment: async (): Promise<IncentiveProgramEnrollmentResponse | null> => {
      await refresh();
      return null;
    },
  };
  const rewardsEnrollment: RewardsEnrollmentProviderType = {
    rewardsEnrollmentResponse,
    rewardsEnrollmentLoading: false,
    refreshRewardsEnrollment: async () => {
      await refresh();
      return rewardsEnrollmentResponse ?? { isEnrolled: false };
    },
  };

  const routes = getRoutes({
    user,
    rewardsEnrollment,
    incentiveProgramEnrollment,
  });

  return (
    <>
      <NavigationMenu
        navItems={routes
          .filter(item => !('hideFromNav' in item && item.hideFromNav))
          .map(({ component, ...navProps }) => ({ ...navProps }))}
      />
      <div className={styles.container}>
        <Header />
        <main
          role="main"
          className={styles.routeContainer}
          data-testid="core-routes"
        >
          {user.activated ? (
            <Switch>
              {routes.map((item, i) => {
                const Component = item.component;
                return (
                  <Route
                    exact={i === 0} // only exact on home
                    path={item.path}
                    render={props => <Component {...props} />}
                    key={item.path}
                  />
                );
              })}
              <Redirect to="/quotes" />
            </Switch>
          ) : (
            <StopScreen />
          )}
        </main>
      </div>
    </>
  );
};
